import { useEffect, useState } from "react";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";

type UseDualApprovalSelectionReturn = {
  selectedIds: Record<string, boolean>;
  selectedApprovals: PaginatedDualApproval[];
  totalSelected: number;
  allSelectableAreSelected: boolean;
  handleToggleCheckbox: (dualApprovalId: string) => void;
  handleToggleCheckboxHeader: (isSelected: boolean) => void;
  deselectItem: (dualApprovalId: string) => void;
  setSelectedOnly: (ids: string[]) => void;
};

const useDualApprovalSelection = (
  dualApprovals: PaginatedDualApproval[]
): UseDualApprovalSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelectedIds((previousSelectedIds) => {
      const currentIds = new Set<string>(
        dualApprovals.map(({ id }) => String(id))
      );
      return Object.entries(previousSelectedIds).reduce<
        Record<string, boolean>
      >((updated, [id, isSelected]) => {
        if (currentIds.has(id) && isSelected) {
          updated[id] = true;
        }
        return updated;
      }, {});
    });
  }, [dualApprovals]);

  const selectableApprovals = dualApprovals.filter(
    ({ isOwnRequest }) => !isOwnRequest
  );
  const selectedApprovals = dualApprovals.filter(({ id }) => selectedIds[id]);
  const totalSelected = selectedApprovals.length;
  const allSelectableAreSelected =
    selectableApprovals.length > 0 &&
    selectableApprovals.every(({ id }) => Boolean(selectedIds[id]));

  const handleToggleCheckboxHeader = (isSelected: boolean) => {
    if (!isSelected) {
      setSelectedIds({});
      return;
    }

    setSelectedIds(
      selectableApprovals.reduce<Record<string, boolean>>((selected, item) => {
        selected[item.id] = true;
        return selected;
      }, {})
    );
  };

  const handleToggleCheckbox = (dualApprovalId: string) => {
    setSelectedIds((previousSelectedIds) => {
      const updated = { ...previousSelectedIds };
      if (updated[dualApprovalId]) {
        delete updated[dualApprovalId];
      } else {
        updated[dualApprovalId] = true;
      }
      return updated;
    });
  };

  const deselectItem = (dualApprovalId: string) => {
    setSelectedIds((previousSelectedIds) => {
      const updated = { ...previousSelectedIds };
      delete updated[dualApprovalId];
      return updated;
    });
  };

  const setSelectedOnly = (ids: string[]) => {
    setSelectedIds(
      ids.reduce<Record<string, boolean>>((selected, id) => {
        selected[id] = true;
        return selected;
      }, {})
    );
  };

  return {
    selectedIds,
    selectedApprovals,
    totalSelected,
    allSelectableAreSelected,
    handleToggleCheckbox,
    handleToggleCheckboxHeader,
    deselectItem,
    setSelectedOnly,
  };
};

export default useDualApprovalSelection;
